import React, {useState, useEffect, useRef} from 'react';
import {Menu, X, Calendar, Twitter, Instagram, FileText, Send, Loader2, Archive, Sparkles, Copy} from 'lucide-react';

function App() {
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [activeTab, setActiveTab] = useState('dashboard');
    const [generatedContent, setGeneratedContent] = useState({
        x: null,
        instagram: null,
        blog: null
    });
    const [chatMessages, setChatMessages] = useState([]);
    const [chatInput, setChatInput] = useState('');
    const [isGenerating, setIsGenerating] = useState({
        x: false,
        instagram: false,
        blog: false
    });
    const [isChatLoading, setIsChatLoading] = useState(false);
    const [showEmojiPalette, setShowEmojiPalette] = useState(false);
    const [copiedEmoji, setCopiedEmoji] = useState(null);
    const chatEndRef = useRef(null);

    const colors = {
        nflBlue: '#1e3a8a',
        nflOrange: '#f97316'
    };

    const emojis = {
        topic: {emoji: '🏈', label: 'Topic'},
        data: {emoji: '📈', label: 'Data'},
        insight: {emoji: '🧠', label: 'Insight'},
        accuracy: {emoji: '🎯', label: 'Accuracy'},
        hotTake: {emoji: '🔥', label: 'Hot Take'},
        breakout: {emoji: '🚀', label: 'Breakout'},
        tagline: {emoji: '📝', label: 'Tagline'}
    };

    const dailyBrief = {
        date: 'Friday, October 3, 2025',
        time: '2:28 PM EDT',
        location: 'New York, New York',
        focus: 'Fantasy Friday & Weekend Primer',
        mainBrief: `**Daily Content Brief: October 3, 2025**

* **Date:** Friday, October 3, 2025
* **Time:** 2:28 PM EDT
* **Location:** New York, New York
* **Strategic Focus:** "Fantasy Friday & Weekend Primer." The goal is to provide final, high-value fantasy football advice and preview key Sunday matchups.
* **Topical Context:** Final injury reports for Week 5 are dropping. This is the last chance for fantasy managers to make informed decisions.
* **GEO-Targeting Angle:** The Jets are facing a tough road test against the Denver Broncos, and the Giants are preparing for a divisional matchup against the Dallas Cowboys.`
    };

    const swaySystemInstructions = `You are Sway, an expert AI digital media strategist for "Add It To The List Sports."

BRAND: Confident, analytical, conversational. Target: Smart fantasy football fans.
Colors: NFL Blue (#1e3a8a), Orange (#f97316)
Tagline: "Add it to the list."

CURRENT CONTEXT: ${dailyBrief.mainBrief}`;

    useEffect(() => {
        chatEndRef.current?.scrollIntoView({behavior: 'smooth'});
    }, [chatMessages]);

    const copyEmoji = (emoji) => {
        navigator.clipboard.writeText(emoji);
        setCopiedEmoji(emoji);
        setTimeout(() => setCopiedEmoji(null), 2000);
    };

    const generateContent = async (platform) => {
        setIsGenerating(prev => ({...prev, [platform]: true}));

        let platformPrompt = '';

        if (platform === 'x') {
            platformPrompt = `Generate an X thread (5-7 tweets, <280 chars each) about NFL/fantasy. Use emojis 🏈📈🧠🎯🔥🚀📝. End with "Add it to the list. 📝". 

FORMAT AS JSON:
{
  "tweets": ["tweet 1", "tweet 2", ...],
  "topic": "brief description",
  "engagementHook": "question for audience"
}

ONLY OUTPUT VALID JSON.`;
        } else if (platform === 'instagram') {
            platformPrompt = `Generate Instagram carousel (5 slides). Slide 1: hook question. Slides 2-4: insights. Slide 5: warning + CTA.

FORMAT AS JSON:
{
  "slides": [
    {
      "slideNumber": 1,
      "title": "hook",
      "content": "text",
      "visualNote": "design note"
    },
    ...
  ],
  "caption": "full caption with hashtags",
  "topic": "brief description"
}

ONLY OUTPUT VALID JSON.`;
        } else {
            platformPrompt = `Generate blog post outline with H1/H2/H3 structure. Title as question.

FORMAT AS JSON:
{
  "title": "question title",
  "metaDescription": "SEO description",
  "structure": [
    {
      "heading": "heading text",
      "level": 1,
      "content": "content"
    },
    ...
  ],
  "schemaMarkup": "recommended schema"
}

ONLY OUTPUT VALID JSON.`;
        }

        try {
            const response = await fetch('https://api.anthropic.com/v1/messages', {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({
                    model: 'claude-sonnet-4-20250514',
                    max_tokens: 2000,
                    messages: [{
                        role: 'user',
                        content: swaySystemInstructions + '\n\n' + platformPrompt
                    }]
                })
            });

            const data = await response.json();
            let responseText = data.content[0].text.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim();
            const parsedContent = JSON.parse(responseText);
            setGeneratedContent(prev => ({...prev, [platform]: parsedContent}));
        } catch (error) {
            console.error('Error:', error);
            setGeneratedContent(prev => ({...prev, [platform]: {error: 'Failed to generate. Try again.'}}));
        } finally {
            setIsGenerating(prev => ({...prev, [platform]: false}));
        }
    };

    const handleChatSend = async () => {
        if (!chatInput.trim()) return;

        const userMessage = {role: 'user', content: chatInput};
        setChatMessages(prev => [...prev, userMessage]);
        setChatInput('');
        setIsChatLoading(true);

        try {
            const conversationHistory = chatMessages.map(msg => ({
                role: msg.role,
                content: msg.content
            }));

            let contentContext = '\n\nGENERATED CONTENT:\n';
            if (generatedContent.x) contentContext += `X: ${JSON.stringify(generatedContent.x)}\n`;
            if (generatedContent.instagram) contentContext += `IG: ${JSON.stringify(generatedContent.instagram)}\n`;
            if (generatedContent.blog) contentContext += `Blog: ${JSON.stringify(generatedContent.blog)}\n`;

            const response = await fetch('https://api.anthropic.com/v1/messages', {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({
                    model: 'claude-sonnet-4-20250514',
                    max_tokens: 1500,
                    messages: [
                        {
                            role: 'user',
                            content: swaySystemInstructions + contentContext + '\n\nYou are editing content with the user. Maintain Sway persona.\n\nHISTORY:\n' + JSON.stringify(conversationHistory)
                        },
                        userMessage
                    ]
                })
            });

            const data = await response.json();
            setChatMessages(prev => [...prev, {role: 'assistant', content: data.content[0].text}]);
        } catch (error) {
            console.error('Error:', error);
            setChatMessages(prev => [...prev, {role: 'assistant', content: 'Error. Try again.'}]);
        } finally {
            setIsChatLoading(false);
        }
    };

    return (
        <div className="flex h-screen bg-gray-900 text-gray-100">
            {/* Sidebar */}
            <div
                className={`${sidebarOpen ? 'w-64' : 'w-0'} transition-all duration-300 bg-gray-800 border-r border-gray-700 overflow-hidden`}>
                <div className="p-4">
                    <div className="flex items-center gap-2 mb-8">
                        <div className="w-10 h-10 rounded-lg flex items-center justify-center text-xl"
                             style={{backgroundColor: colors.nflOrange}}>
                            🏈
                        </div>
                        <div>
                            <h1 className="font-bold text-sm">Add It To The List</h1>
                            <p className="text-xs text-gray-400">Content Dashboard</p>
                        </div>
                    </div>

                    <nav className="space-y-2">
                        <button
                            onClick={() => setActiveTab('dashboard')}
                            className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-colors ${activeTab === 'dashboard' ? 'text-white' : 'hover:bg-gray-700'}`}
                            style={activeTab === 'dashboard' ? {backgroundColor: colors.nflBlue} : {}}
                        >
                            <Calendar size={20}/>
                            <span>Dashboard</span>
                        </button>
                        <button
                            onClick={() => setActiveTab('archive')}
                            className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-colors ${activeTab === 'archive' ? 'text-white' : 'hover:bg-gray-700'}`}
                            style={activeTab === 'archive' ? {backgroundColor: colors.nflBlue} : {}}
                        >
                            <Archive size={20}/>
                            <span>"The List" Archive</span>
                        </button>
                        <button
                            onClick={() => setShowEmojiPalette(!showEmojiPalette)}
                            className="w-full flex items-center gap-3 px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
                        >
                            <Sparkles size={20}/>
                            <span>Emoji Palette</span>
                        </button>
                    </nav>

                    {showEmojiPalette && (
                        <div className="mt-4 p-3 bg-gray-700 rounded-lg">
                            <p className="text-xs text-gray-400 mb-2">Click to copy:</p>
                            <div className="grid grid-cols-2 gap-2">
                                {Object.entries(emojis).map(([key, {emoji, label}]) => (
                                    <button
                                        key={key}
                                        onClick={() => copyEmoji(emoji)}
                                        className="flex items-center gap-2 p-2 bg-gray-600 hover:bg-gray-500 rounded text-sm transition-colors"
                                    >
                                        <span className="text-lg">{emoji}</span>
                                        <span className="text-xs">{label}</span>
                                        {copiedEmoji === emoji && (
                                            <span className="text-green-400 text-xs ml-auto">✓</span>
                                        )}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Main Content */}
            <div className="flex-1 flex flex-col overflow-hidden">
                <header className="bg-gray-800 border-b border-gray-700 p-4 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => setSidebarOpen(!sidebarOpen)}
                            className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
                        >
                            {sidebarOpen ? <X size={24}/> : <Menu size={24}/>}
                        </button>
                        <div>
                            <h2 className="font-bold">Sway Content Studio</h2>
                            <p className="text-sm text-gray-400">{dailyBrief.date}</p>
                        </div>
                    </div>
                    <span className="px-3 py-1 rounded-full text-xs font-medium"
                          style={{backgroundColor: colors.nflOrange}}>
            {dailyBrief.focus}
          </span>
                </header>

                {activeTab === 'dashboard' && (
                    <div className="flex-1 overflow-auto p-6">
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
                            <div className="lg:col-span-1 bg-gray-800 rounded-lg border border-gray-700 p-6">
                                <div className="flex items-center gap-2 mb-4">
                                    <Calendar size={24} style={{color: colors.nflOrange}}/>
                                    <h3 className="font-bold text-lg">Today's Brief</h3>
                                </div>

                                <div
                                    className="bg-amber-50 text-gray-900 p-4 rounded-lg shadow-inner border-2 border-amber-200 mb-4"
                                    style={{
                                        backgroundImage: 'linear-gradient(to bottom, #fffbeb 0%, #fef3c7 100%)',
                                        fontFamily: 'Georgia, serif'
                                    }}>
                                    <div className="text-sm leading-relaxed whitespace-pre-line">
                                        {dailyBrief.mainBrief}
                                    </div>
                                </div>
                            </div>

                            <div className="lg:col-span-2 space-y-6">
                                <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
                                    <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                                        <span>🧠</span>
                                        Generate Content
                                    </h3>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <button
                                            onClick={() => generateContent('x')}
                                            disabled={isGenerating.x}
                                            className="flex flex-col items-center gap-3 p-6 bg-gray-700 hover:bg-gray-600 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            {isGenerating.x ? (
                                                <Loader2 size={32} className="animate-spin"/>
                                            ) : (
                                                <Twitter size={32} style={{color: colors.nflOrange}}/>
                                            )}
                                            <div className="text-center">
                                                <div className="font-bold">X Thread</div>
                                                <div className="text-xs text-gray-400">Live Pulse</div>
                                            </div>
                                        </button>

                                        <button
                                            onClick={() => generateContent('instagram')}
                                            disabled={isGenerating.instagram}
                                            className="flex flex-col items-center gap-3 p-6 bg-gray-700 hover:bg-gray-600 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            {isGenerating.instagram ? (
                                                <Loader2 size={32} className="animate-spin"/>
                                            ) : (
                                                <Instagram size={32} style={{color: colors.nflOrange}}/>
                                            )}
                                            <div className="text-center">
                                                <div className="font-bold">Instagram</div>
                                                <div className="text-xs text-gray-400">Polished Post</div>
                                            </div>
                                        </button>

                                        <button
                                            onClick={() => generateContent('blog')}
                                            disabled={isGenerating.blog}
                                            className="flex flex-col items-center gap-3 p-6 bg-gray-700 hover:bg-gray-600 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            {isGenerating.blog ? (
                                                <Loader2 size={32} className="animate-spin"/>
                                            ) : (
                                                <FileText size={32} style={{color: colors.nflOrange}}/>
                                            )}
                                            <div className="text-center">
                                                <div className="font-bold">Blog Post</div>
                                                <div className="text-xs text-gray-400">Deep Dive</div>
                                            </div>
                                        </button>
                                    </div>
                                </div>

                                {(generatedContent.x || generatedContent.instagram || generatedContent.blog) && (
                                    <div
                                        className="bg-gray-800 rounded-lg border border-gray-700 p-6 max-h-96 overflow-y-auto">
                                        <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                                            <span>📈</span>
                                            Generated Content
                                        </h3>

                                        {generatedContent.x && !generatedContent.x.error && (
                                            <div className="mb-6">
                                                <div className="flex items-center gap-2 mb-3">
                                                    <Twitter size={20} style={{color: colors.nflOrange}}/>
                                                    <span className="font-bold">X Thread</span>
                                                </div>
                                                <div className="space-y-2">
                                                    {generatedContent.x.tweets?.map((tweet, idx) => (
                                                        <div key={idx} className="bg-gray-700 p-3 rounded-lg">
                                                            <div
                                                                className="text-xs text-gray-400 mb-1">Tweet {idx + 1}</div>
                                                            <div className="text-sm">{tweet}</div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {generatedContent.instagram && !generatedContent.instagram.error && (
                                            <div className="mb-6">
                                                <div className="flex items-center gap-2 mb-3">
                                                    <Instagram size={20} style={{color: colors.nflOrange}}/>
                                                    <span className="font-bold">Instagram Carousel</span>
                                                </div>
                                                <div className="space-y-3">
                                                    {generatedContent.instagram.slides?.map((slide, idx) => (
                                                        <div key={idx} className="bg-gray-700 p-4 rounded-lg">
                                                            <div
                                                                className="font-bold mb-2">Slide {slide.slideNumber}: {slide.title}</div>
                                                            <div className="text-sm mb-2">{slide.content}</div>
                                                            <div
                                                                className="text-xs text-gray-400 italic">{slide.visualNote}</div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {generatedContent.blog && !generatedContent.blog.error && (
                                            <div>
                                                <div className="flex items-center gap-2 mb-3">
                                                    <FileText size={20} style={{color: colors.nflOrange}}/>
                                                    <span className="font-bold">Blog Post Outline</span>
                                                </div>
                                                <div className="space-y-3">
                                                    <div className="bg-gray-700 p-4 rounded-lg">
                                                        <div
                                                            className="font-bold text-lg mb-2">{generatedContent.blog.title}</div>
                                                        <div
                                                            className="text-sm text-gray-400">{generatedContent.blog.metaDescription}</div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
                            <div className="p-4 border-b border-gray-700 flex items-center gap-2">
                                <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm"
                                     style={{backgroundColor: colors.nflBlue}}>
                                    🧠
                                </div>
                                <div>
                                    <h3 className="font-bold">Chat with Sway</h3>
                                    <p className="text-xs text-gray-400">Edit and refine your content</p>
                                </div>
                            </div>

                            <div className="h-64 overflow-y-auto p-4 space-y-3">
                                {chatMessages.length === 0 && (
                                    <div className="text-center text-gray-500 py-8">
                                        <p>Start a conversation to edit and refine your content</p>
                                    </div>
                                )}
                                {chatMessages.map((msg, idx) => (
                                    <div key={idx}
                                         className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                                        <div
                                            className={`max-w-3xl p-3 rounded-lg ${msg.role === 'user' ? 'text-white' : 'bg-gray-700'}`}
                                            style={msg.role === 'user' ? {backgroundColor: colors.nflBlue} : {}}>
                                            <div className="text-sm whitespace-pre-line">{msg.content}</div>
                                        </div>
                                    </div>
                                ))}
                                {isChatLoading && (
                                    <div className="flex justify-start">
                                        <div className="bg-gray-700 p-3 rounded-lg">
                                            <Loader2 size={16} className="animate-spin"/>
                                        </div>
                                    </div>
                                )}
                                <div ref={chatEndRef}/>
                            </div>

                            <div className="p-4 border-t border-gray-700">
                                <div className="flex gap-2">
                                    <input
                                        type="text"
                                        value={chatInput}
                                        onChange={(e) => setChatInput(e.target.value)}
                                        onKeyPress={(e) => e.key === 'Enter' && handleChatSend()}
                                        placeholder="Ask Sway to edit or refine your content..."
                                        className="flex-1 bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        disabled={isChatLoading}
                                    />
                                    <button
                                        onClick={handleChatSend}
                                        disabled={!chatInput.trim() || isChatLoading}
                                        className="px-6 py-2 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                        style={{backgroundColor: colors.nflOrange}}
                                    >
                                        <Send size={20}/>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === 'archive' && (
                    <div className="flex-1 overflow-auto p-6">
                        <div className="bg-gray-800 rounded-lg border border-gray-700 p-8 text-center">
                            <Archive size={48} className="mx-auto mb-4 text-gray-600"/>
                            <h3 className="font-bold text-xl mb-2">The List Archive</h3>
                            <p className="text-gray-400">Coming soon: View your published content history and
                                                         performance metrics</p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default App;
